import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, Loader2, LogOut, User, Shield, BarChart3, CheckCircle, XCircle,
  Archive, Eye, Search, ChevronRight, ChevronLeft, Building2, Globe, TrendingUp,
  FileText, Database, RefreshCw, BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ensureFreshToken } from "@/lib/sessionKeepAlive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function portalAxios(token) {
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
}

const ROLE_LABELS = {
  microsoft_manager: "Microsoft Manager",
  admin: "MyGoals Admin",
  super_admin: "Super Admin",
};

// ─── Staging Tab ──────────────────────────────────────────────
function StagingReview({ token }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await portalAxios(token).get(`${API}/portal/staging`);
      setResources(res.data);
    } catch {
      toast.error("Failed to load staging resources");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === resources.length) setSelected(new Set());
    else setSelected(new Set(resources.map(r => r.resource_id)));
  };

  const handleBulk = async (action) => {
    if (selected.size === 0) return;
    setActionLoading(true);
    try {
      await portalAxios(token).post(`${API}/portal/staging/bulk`, {
        resource_ids: Array.from(selected), action,
      });
      toast.success(`${selected.size} resources ${action}d`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingle = async (resourceId, action) => {
    try {
      await portalAxios(token).post(`${API}/portal/staging/${resourceId}`, { action });
      toast.success(`Resource ${action}d`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#194449]" /></div>;

  return (
    <div className="space-y-4">
      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-[#194449]/5 rounded-lg p-3">
          <span className="text-sm font-medium text-[#091F2C]">{selected.size} selected</span>
          <Button size="sm" onClick={() => handleBulk("approve")} disabled={actionLoading}
            className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs" data-testid="bulk-approve-btn">
            {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Approve</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulk("reject")} disabled={actionLoading}
            className="text-red-600 border-red-200 hover:bg-red-50 rounded-full text-xs" data-testid="bulk-reject-btn">
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulk("archive")} disabled={actionLoading}
            className="text-[#64748B] rounded-full text-xs" data-testid="bulk-archive-btn">
            <Archive className="w-3 h-3 mr-1" /> Archive
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        <div className="p-4 border-b border-black/5 flex items-center justify-between">
          <h3 className="font-semibold text-[#091F2C] text-sm" data-testid="staging-heading">
            <Database className="w-4 h-4 inline mr-2 text-[#194449]" />
            Staging Resources ({resources.length})
          </h3>
          <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-staging-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        {resources.length === 0 ? (
          <div className="text-center py-12 text-sm text-[#64748B]">No resources pending review.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F3F5]">
                <TableHead className="w-10">
                  <input type="checkbox" checked={selected.size === resources.length && resources.length > 0}
                    onChange={toggleAll} className="rounded" data-testid="select-all-staging" />
                </TableHead>
                <TableHead className="text-xs">ID</TableHead>
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Source</TableHead>
                <TableHead className="text-xs">Products</TableHead>
                <TableHead className="text-xs">Stage</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.map((r) => (
                <TableRow key={r.resource_id} className="hover:bg-[#F4F3F5]/50">
                  <TableCell>
                    <input type="checkbox" checked={selected.has(r.resource_id)}
                      onChange={() => toggleSelect(r.resource_id)} className="rounded" />
                  </TableCell>
                  <TableCell className="text-xs font-mono">{r.resource_id}</TableCell>
                  <TableCell className="text-xs max-w-[200px]">
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        className="hover:text-[#194449] hover:underline truncate block" title={r.title}>{r.title}</a>
                    ) : <span className="truncate block" title={r.title}>{r.title}</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{r.provider_name || r.source_connector || "-"}</Badge></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[140px]">
                      {(r.hero_products || []).slice(0, 2).map(p => (
                        <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/10 text-[#194449]">{p.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-[10px]">{(r.course_stage || "").replace(/_/g, " ")}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] border-0 ${r.status === "Staging" ? "bg-yellow-100 text-yellow-700" : r.status === "archive_candidate" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-green-600 hover:bg-green-50"
                        onClick={() => handleSingle(r.resource_id, "approve")} data-testid={`approve-${r.resource_id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50"
                        onClick={() => handleSingle(r.resource_id, "reject")} data-testid={`reject-${r.resource_id}`}>
                        <XCircle className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ─── CSV Upload Tab ───────────────────────────────────────────
function CsvUpload({ token }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const freshToken = await ensureFreshToken({ force: true });
      const res = await axios.post(`${API}/portal/resources/ingest-external`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${freshToken || token}` },
      });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-black/5 p-6">
        <h3 className="font-semibold text-[#091F2C] mb-2" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="csv-upload-heading">
          Upload External Resources
        </h3>
        <p className="text-sm text-[#64748B] mb-4">
          Upload a CSV file with external resources. Resources will be placed in Staging for review before publishing.
        </p>
        <div className="bg-[#F4F3F5] rounded-lg p-4 mb-4">
          <p className="text-xs font-medium text-[#091F2C] mb-2">Required columns:</p>
          <code className="text-xs text-[#194449]">resource_title, resource_url</code>
          <p className="text-xs text-[#64748B] mt-2">Optional: resource_description, source_owner, hero_products, solution_area, target_roles, course_stage, skill_level, content_format, estimated_duration_minutes, tags, notes</p>
        </div>
        <label className="cursor-pointer inline-block" data-testid="csv-upload-btn">
          <div className="inline-flex items-center gap-2 bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-6 py-2.5 text-sm font-medium transition-all shadow-md">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading..." : "Upload CSV / Excel"}
          </div>
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {result && (
        <div className="bg-white rounded-xl border border-black/5 p-6" data-testid="upload-result">
          <h4 className="font-semibold text-sm text-[#091F2C] mb-3">Upload Result</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-[#F4F3F5] rounded-lg p-3 text-center">
              <div className="text-lg font-semibold text-[#091F2C]">{result.total_rows}</div>
              <div className="text-xs text-[#64748B]">Total Rows</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-lg font-semibold text-green-700">{result.staged}</div>
              <div className="text-xs text-green-600">Staged</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <div className="text-lg font-semibold text-yellow-700">{result.skipped}</div>
              <div className="text-xs text-yellow-600">Skipped</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-lg font-semibold text-blue-700">{result.auto_published || 0}</div>
              <div className="text-xs text-blue-600">Auto-Published</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
              {result.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Anonymized Analytics Tab ─────────────────────────────────
function AnonymizedAnalytics({ token }) {
  const [overview, setOverview] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [orgInsights, setOrgInsights] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = portalAxios(token);
      const [ov, oc, org, ua] = await Promise.all([
        api.get(`${API}/portal/analytics/usage-overview?days=${days}`),
        api.get(`${API}/portal/analytics/assessment-outcomes?days=${days}`),
        api.get(`${API}/portal/analytics/org-insights?days=${days}`),
        api.get(`${API}/portal/analytics/user-activity?days=${days}`),
      ]);
      setOverview(ov.data);
      setOutcomes(oc.data);
      setOrgInsights(org.data);
      setUserActivity(ua.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#194449]" /></div>;

  return (
    <div className="space-y-6">
      {/* Period filter */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#091F2C]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="analytics-heading">
          Usage Analytics (Anonymized)
        </h3>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[140px]" data-testid="analytics-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Events", value: overview.total_events, icon: BarChart3, color: "#194449" },
            { label: "Unique Users", value: overview.unique_users, icon: Building2, color: "#07641D" },
            { label: "Total Sessions", value: outcomes?.total_sessions || 0, icon: BookOpen, color: "#2563EB" },
            { label: "Avg Rating", value: outcomes?.avg_rating ? `${outcomes.avg_rating}/5` : "N/A", icon: TrendingUp, color: "#D97706" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-black/5 p-5 shadow-sm" data-testid={`analytics-stat-${i}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}12` }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-2xl font-semibold text-[#091F2C]">{s.value}</div>
                  <div className="text-xs text-[#64748B]">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assessment Outcomes */}
      {outcomes && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-black/5 p-5">
            <h4 className="text-sm font-semibold text-[#091F2C] mb-3">By Role</h4>
            {Object.entries(outcomes.by_role || {}).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between py-1.5 border-b border-black/5 last:border-0">
                <span className="text-xs text-[#64748B] capitalize">{role.replace(/_/g, " ")}</span>
                <span className="text-xs font-medium text-[#091F2C]">{count}</span>
              </div>
            ))}
            {Object.keys(outcomes.by_role || {}).length === 0 && <p className="text-xs text-[#94A3B8]">No data</p>}
          </div>
          <div className="bg-white rounded-xl border border-black/5 p-5">
            <h4 className="text-sm font-semibold text-[#091F2C] mb-3">By Outcome</h4>
            {Object.entries(outcomes.by_outcome || {}).map(([outcome, count]) => (
              <div key={outcome} className="flex items-center justify-between py-1.5 border-b border-black/5 last:border-0">
                <span className="text-xs text-[#64748B] capitalize">{outcome.replace(/_/g, " ")}</span>
                <span className="text-xs font-medium text-[#091F2C]">{count}</span>
              </div>
            ))}
            {Object.keys(outcomes.by_outcome || {}).length === 0 && <p className="text-xs text-[#94A3B8]">No data</p>}
          </div>
        </div>
      )}

      {/* Organization Insights */}
      {orgInsights && orgInsights.organizations?.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
          <div className="p-4 border-b border-black/5">
            <h4 className="text-sm font-semibold text-[#091F2C]">Organization Insights</h4>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F3F5]">
                <TableHead className="text-xs">Domain</TableHead>
                <TableHead className="text-xs">Events</TableHead>
                <TableHead className="text-xs">Unique Users</TableHead>
                <TableHead className="text-xs">Plans Generated</TableHead>
                <TableHead className="text-xs">Completion Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgInsights.organizations.slice(0, 20).map((org) => (
                <TableRow key={org.domain}>
                  <TableCell className="text-xs font-medium">{org.domain}</TableCell>
                  <TableCell className="text-xs">{org.total_events}</TableCell>
                  <TableCell className="text-xs">{org.unique_users}</TableCell>
                  <TableCell className="text-xs">{org.plans_generated}</TableCell>
                  <TableCell className="text-xs">{org.completion_rate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* User Activity (Anonymized) */}
      {userActivity && userActivity.users?.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
          <div className="p-4 border-b border-black/5">
            <h4 className="text-sm font-semibold text-[#091F2C]">
              User Activity (Anonymized — No PII)
            </h4>
            <p className="text-xs text-[#94A3B8] mt-0.5">Shows LMS ID, organization, country, and user type only</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F3F5]">
                <TableHead className="text-xs">LMS ID</TableHead>
                <TableHead className="text-xs">Organization</TableHead>
                <TableHead className="text-xs">Country</TableHead>
                <TableHead className="text-xs">User Type</TableHead>
                <TableHead className="text-xs">Plans Generated</TableHead>
                <TableHead className="text-xs">Plans Saved</TableHead>
                <TableHead className="text-xs">Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userActivity.users.map((u, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs font-mono">{u.lms_id || "-"}</TableCell>
                  <TableCell className="text-xs">{u.organization || "-"}</TableCell>
                  <TableCell className="text-xs">{u.country || "-"}</TableCell>
                  <TableCell>
                    {u.user_type && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600">{u.user_type}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{u.plans_generated}</TableCell>
                  <TableCell className="text-xs">{u.plans_saved}</TableCell>
                  <TableCell className="text-xs text-[#94A3B8]">
                    {u.last_activity ? new Date(u.last_activity).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Main Portal Page ─────────────────────────────────────────
export default function ManagerPortalPage() {
  const navigate = useNavigate();
  const { user, logout, token, isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate("/login");
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user && !["microsoft_manager", "admin", "super_admin"].includes(user.role)) {
      toast.error("You don't have access to the Manager Portal");
      navigate("/my-plans");
    }
  }, [user, navigate]);

  const handleLogout = () => { logout(); navigate("/"); };

  if (authLoading || !user) {
    return <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#194449]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FFF8F3]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#194449] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="logo-home-link-portal">
              <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-6 brightness-0 invert" />
              <div className="w-px h-6 bg-white/30" />
              <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                MyGoals <span className="text-white/60 font-normal text-sm ml-1">Manager Portal</span>
              </span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/my-plans")} className="text-sm text-white/70 hover:text-white transition-colors hidden md:block" data-testid="my-plans-link-portal">
              My Plans
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-sm text-white/90">{user.first_name || user.email?.split("@")[0]}</span>
                <span className="text-[10px] text-white/50 ml-2 px-1.5 py-0.5 rounded bg-white/10">
                  {ROLE_LABELS[user.role] || user.role}
                </span>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Tabs defaultValue="staging" className="w-full">
          <TabsList className="bg-white border border-black/5 p-1 rounded-lg mb-6 flex-wrap">
            <TabsTrigger value="staging" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="portal-tab-staging">
              <Database className="w-4 h-4 mr-2" /> Staging Review
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="portal-tab-upload">
              <Upload className="w-4 h-4 mr-2" /> CSV Upload
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="portal-tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="staging"><StagingReview token={token} /></TabsContent>
          <TabsContent value="upload"><CsvUpload token={token} /></TabsContent>
          <TabsContent value="analytics"><AnonymizedAnalytics token={token} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
